import { useState, useRef, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Send, Loader2, Anchor } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import captainAvatar from "@/assets/captain-avatar.png";
import analytics from "@/lib/segment";
import { useCaptainConversation, type Message } from "@/hooks/useCaptainConversation";
import { getAIApiUrl } from "@/lib/apiRoutes";

const CHAT_URL = getAIApiUrl("captain-chat");

async function streamChat({
  messages,
  token,
  onDelta,
  onDone,
  onError,
}: {
  messages: Message[];
  token: string;
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (err: string) => void;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ messages }),
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ error: "Unknown error" }));
    onError(body.error || `Error ${resp.status}`);
    return;
  }

  if (!resp.body) {
    onError("No response stream");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();

  // Parse SSE (OpenAI-format) from Supabase edge function
  let buffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done: readerDone, value } = await reader.read();
    if (readerDone) break;
    buffer += decoder.decode(value, { stream: true });

    let newlineIdx: number;
    while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, newlineIdx);
      buffer = buffer.slice(newlineIdx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onDelta(content);
      } catch {
        buffer = line + "\n" + buffer;
        break;
      }
    }
  }

  if (buffer.trim()) {
    for (let raw of buffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onDelta(content);
      } catch { /* ignore */ }
    }
  }

  onDone();
}

const SUGGESTED_PROMPTS = [
  "How is my fleet performing?",
  "Which vehicle earns the most?",
  "What should I do first?",
  "Tell me about subscription plans",
];

const THINKING_PHRASES_BY_TOPIC: Record<string, string[]> = {
  earnings: ["Digging through your earnings…", "Crunching the revenue numbers…", "Scanning trip payouts…"],
  expenses: ["Reviewing your expenses…", "Tallying up the costs…", "Checking your spending patterns…"],
  vehicles: ["Looking at your fleet…", "Checking vehicle performance…", "Inspecting the lineup…"],
  maintenance: ["Checking maintenance logs…", "Reviewing service records…", "Scanning upcoming tasks…"],
  subscription: ["Looking up plan details…", "Checking subscription options…", "Reviewing pricing tiers…"],
  general: ["Charting a course…", "Consulting the navigation maps…", "Scanning the horizon…", "Checking the ship's log…", "Running the numbers…"],
};

function getThinkingPhrases(userMessage: string): string[] {
  const msg = userMessage.toLowerCase();
  const matched: string[] = [];
  if (/earn|revenue|trip|income|payout/.test(msg)) matched.push(...THINKING_PHRASES_BY_TOPIC.earnings);
  if (/expens|cost|spend|bill/.test(msg)) matched.push(...THINKING_PHRASES_BY_TOPIC.expenses);
  if (/vehicle|car|fleet|perform/.test(msg)) matched.push(...THINKING_PHRASES_BY_TOPIC.vehicles);
  if (/mainten|service|oil|tire|repair/.test(msg)) matched.push(...THINKING_PHRASES_BY_TOPIC.maintenance);
  if (/plan|subscri|pric|tier|billing|upgrade/.test(msg)) matched.push(...THINKING_PHRASES_BY_TOPIC.subscription);
  if (matched.length === 0) matched.push(...THINKING_PHRASES_BY_TOPIC.general);
  matched.push(...THINKING_PHRASES_BY_TOPIC.general);
  return matched;
}

export default function CaptainAI() {
  const {
    messages,
    setMessages,
    isLoadingHistory,
    ensureConversation,
    saveMessage,
    getMessagesForLLM,
    WELCOME_MESSAGE,
  } = useCaptainConversation();

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingPhrase, setThinkingPhrase] = useState("");
  const thinkingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userScrolledRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    analytics.track("Captain AI Viewed");
  }, []);

  // Auto-scroll to bottom unless user has manually scrolled up
  useEffect(() => {
    if (scrollRef.current && !userScrolledRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Detect manual scroll-up to disable auto-scroll for the session
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      if (!atBottom) {
        userScrolledRef.current = true;
      }
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isLoadingHistory]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({ title: "Please log in", description: "You need to be signed in to use Captain AI.", variant: "destructive" });
      return;
    }

    const userId = session.user.id;
    const userMsg: Message = { role: "user", content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    // Start rotating thinking phrases
    const phrases = getThinkingPhrases(trimmed);
    let phraseIdx = 0;
    setThinkingPhrase(phrases[0]);
    thinkingIntervalRef.current = setInterval(() => {
      phraseIdx = (phraseIdx + 1) % phrases.length;
      setThinkingPhrase(phrases[phraseIdx]);
    }, 2500);

    const stopThinking = () => {
      if (thinkingIntervalRef.current) {
        clearInterval(thinkingIntervalRef.current);
        thinkingIntervalRef.current = null;
      }
      setThinkingPhrase("");
    };

    analytics.track("Captain AI Message Sent", { message_length: trimmed.length });

    // Save user message to DB
    let convoId: string;
    try {
      convoId = await ensureConversation(userId);
      await saveMessage(userId, convoId, "user", trimmed);
    } catch {
      convoId = "";
    }

    let assistantSoFar = "";

    const upsertAssistant = (chunk: string) => {
      stopThinking();
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && prev.length > newMessages.length) {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev.slice(0, newMessages.length), { role: "assistant", content: assistantSoFar }];
      });
    };

    const toSend = getMessagesForLLM(newMessages);

    try {
      await streamChat({
        messages: toSend.length > 0 ? toSend : [userMsg],
        token: session.access_token,
        onDelta: (chunk) => upsertAssistant(chunk),
        onDone: async () => {
          stopThinking();
          setIsLoading(false);
          if (convoId && assistantSoFar) {
            try {
              await saveMessage(userId, convoId, "assistant", assistantSoFar);
            } catch { /* non-blocking */ }
          }
        },
        onError: (err) => {
          stopThinking();
          setIsLoading(false);
          toast({ title: "Captain AI Error", description: err, variant: "destructive" });
        },
      });
    } catch {
      stopThinking();
      setIsLoading(false);
      toast({ title: "Connection Error", description: "Failed to reach Captain AI. Please try again.", variant: "destructive" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen p-4 md:p-8">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <Avatar className="h-10 w-10 border-2 border-primary">
            <AvatarImage src={captainAvatar} alt="The Captain" />
            <AvatarFallback><Anchor className="h-5 w-5" /></AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground">The Captain</h1>
            <p className="text-sm text-muted-foreground">Your AI fleet advisor</p>
          </div>
        </div>

        {/* Chat area */}
        <Card className="flex-1 flex flex-col overflow-hidden">
          {isLoadingHistory ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading conversation…</span>
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    {msg.role === "assistant" && (
                      <Avatar className="h-8 w-8 flex-shrink-0 border border-primary/30">
                        <AvatarImage src={captainAvatar} alt="The Captain" />
                        <AvatarFallback><Anchor className="h-4 w-4" /></AvatarFallback>
                      </Avatar>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-3 text-sm ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1 [&>h2]:mt-3 [&>h2]:mb-1 [&>h3]:mt-2 [&>h3]:mb-1">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              table: ({ children, ...props }) => (
                                <div className="overflow-x-auto my-2 rounded-md border border-border">
                                  <table className="w-full text-xs border-collapse" {...props}>{children}</table>
                                </div>
                              ),
                              thead: ({ children, ...props }) => (
                                <thead className="bg-muted/70" {...props}>{children}</thead>
                              ),
                              th: ({ children, ...props }) => (
                                <th className="px-3 py-1.5 text-left font-semibold text-foreground whitespace-nowrap border-b border-border" {...props}>{children}</th>
                              ),
                              td: ({ children, ...props }) => (
                                <td className="px-3 py-1.5 whitespace-nowrap border-b border-border/50" {...props}>{children}</td>
                              ),
                              tr: ({ children, ...props }) => (
                                <tr className="even:bg-muted/30" {...props}>{children}</tr>
                              ),
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && messages[messages.length - 1]?.role === "user" && (
                  <div className="flex gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0 border border-primary/30">
                      <AvatarImage src={captainAvatar} alt="The Captain" />
                      <AvatarFallback><Anchor className="h-4 w-4" /></AvatarFallback>
                    </Avatar>
                    <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
                      {thinkingPhrase && (
                        <span className="text-sm text-muted-foreground italic animate-pulse">
                          {thinkingPhrase}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Suggested prompts (only when just the welcome message) */}
              {messages.length === 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2">
                  {SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => send(prompt)}
                      className="text-xs px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Input */}
          <div className="border-t border-border p-3 flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask The Captain anything..."
              className="min-h-[44px] max-h-[120px] resize-none"
              rows={1}
              disabled={isLoading || isLoadingHistory}
            />
            <Button
              onClick={() => send(input)}
              disabled={!input.trim() || isLoading || isLoadingHistory}
              size="icon"
              className="flex-shrink-0 self-end"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
